import React, { useEffect, useState } from 'react';
import { Check, LoaderCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import useElementPageTop from '@/lib/hooks/useElementPageTop';
import { useTranslation } from 'next-i18next';

type ListItem = {
  status: 'success' | 'loading' | 'waiting';
  text: string;
};

const scoreMap = {
  waiting: 0,
  loading: 10,
  success: 25,
};

export default function Searching({ className }: { className?: string }) {
  const { ref: sectionRef, top } = useElementPageTop<HTMLElement>();
  const { t } = useTranslation();

  const [list, setList] = useState<ListItem[]>([
    { text: t('Evaluating shadowban risk...'), status: 'waiting' },
    { text: t('Reviewing content...'), status: 'waiting' },
    { text: t('Analyzing account activity...'), status: 'waiting' },
    { text: t('Calculating your score...'), status: 'waiting' },
  ]);
  const [processRatio, setProcessRatio] = useState(0);

  useEffect(() => {
    if (list[0].status === 'waiting') {
      const newList = [...list];
      newList[0].status = 'loading';
      setList(newList);
      setProcessRatio(10);
      return;
    }
    const tid = setTimeout(
      () => {
        if (list.at(-1).status === 'loading') return;
        const newList = [...list];
        const loadingIndex = newList.findIndex((item) => item.status === 'loading');
        if (loadingIndex !== -1) {
          newList[loadingIndex].status = 'success';
          if (loadingIndex + 1 < newList.length) {
            newList[loadingIndex + 1].status = 'loading';
          }
        } else {
          const waitingIndex = newList.findIndex((item) => item.status === 'waiting');
          if (waitingIndex !== -1) {
            newList[waitingIndex].status = 'loading';
          }
        }

        const newProcess = newList.reduce((acc, item) => acc + (scoreMap[item.status] || 0), 0);

        setList(newList);
        setProcessRatio(newProcess);
      },
      Math.round(Math.random() * 3000) + 2000
    );

    return () => {
      clearTimeout(tid);
    };
  }, [list]);

  useEffect(() => {
    if (top) {
      console.log(top);
      scrollTo({
        top: top - 200,
        behavior: 'smooth',
      });
    }
  }, [top]);

  return (
    <section ref={sectionRef} className="max-w-[1000px] mx-auto px-4 mt-16">
      <div
        className={cn(
          'flex flex-col items-center pt-[52px]',
          'shadow-[0px_4px_12px_0px_#00000014] rounded-[24px]',
          'pb-[80px] border border-[#D9D9D9]',
          className
        )}
      >
        <div className="text-[#1D2129] text-[48px] font-bold">
          {processRatio}
          <span className="text-[24px]">%</span>
        </div>
        <div className="max-w-[300px] w-full mx-auto mb-[18px] rounded-full overflow-hidden bg-[#EAECF9]">
          <div className="h-1.5 bg-[#6246EA] rounded-full transition-[width]" style={{ width: `${processRatio}%` }} />
        </div>
        <div>
          {list.map((item) => (
            <div className="flex items-center mt-[22px]" key={item.text}>
              {item.status === 'success' && <Check size={28} className="text-[#2ECE5E] mr-[10px]" />}
              {item.status === 'loading' && (
                <LoaderCircle size={28} className="animate-spin text-[#909399] mr-[10px]" />
              )}
              {item.status === 'waiting' && <div className="w-[28px] h-[28px] mr-[10px]" />}
              <div className={`text-[18px] ${item.status === 'success' ? 'text-[#1D2129]' : 'text-[#1D2129]/30'}`}>
                {item.text}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
